import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Scatter } from 'react-chartjs-2';
import { fetchUsers } from '../../utils/userApi';
import RatioCalculator from '../../components/RatioCalculator';
import MultiMonthComparison from '../../components/MultiMonthComparison';
import { fetchMultiMonthAnalytics } from '../../utils/multiMonthAnalytics';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const API_BASE_URL = 'https://system.sales-management.cloud/api';
const DAILY_REPORT_API = `${API_BASE_URL}/daily_report.php`;
const TARGETS_API = `${API_BASE_URL}/targets.php`;
const EXCLUDED_ADMIN_ROLES = ['superadmin', 'admin', 'view_admin'];

const isNumericValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'string') return false;
  return /^-?\d+(\.\d+)?$/.test(value.trim());
};

const buildAnalyticsFromReports = (reports) => {
  const metricsSet = {};
  const summary = {};
  const dailyMap = {};
  const workingDays = {};

  (reports || []).forEach((report) => {
    const date = report?.work_date;
    if (date) {
      workingDays[date] = true;
    }

    const content = report?.daily_report_content;
    if (!content || typeof content !== 'object') return;

    Object.entries(content).forEach(([key, rawValue]) => {
      if (!isNumericValue(rawValue)) return;

      const num = Number(rawValue);
      metricsSet[key] = true;
      summary[key] = (summary[key] || 0) + num;

      if (!dailyMap[date]) {
        dailyMap[date] = { date };
      }
      dailyMap[date][key] = (dailyMap[date][key] || 0) + num;
    });
  });

  const metrics = Object.keys(metricsSet).sort();
  const dailyData = Object.values(dailyMap).sort((a, b) => String(a.date).localeCompare(String(b.date)));

  return {
    metrics,
    summary: {
      ...summary,
      working_days: Object.keys(workingDays).length,
    },
    daily_data: dailyData,
  };
};

function AdminAnalytics() {
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [period, setPeriod] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('');
  const [selectedXMetric, setSelectedXMetric] = useState('');
  const [selectedYMetric, setSelectedYMetric] = useState('');
  const [regressionDayType, setRegressionDayType] = useState('all');
  const [multiMonthData, setMultiMonthData] = useState(null);

  const fetchTargetsForPeriod = useCallback(async (token, userId, periodInfo) => {
    const baseDate = periodInfo?.from ? moment(periodInfo.from) : moment();
    const year = baseDate.year();
    const month = baseDate.month() + 1;

    const params = new URLSearchParams({
      year: String(year),
      month: String(month),
      user_id: String(userId),
    });

    const response = await fetch(`${TARGETS_API}?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();
    if (result.status !== 'success') {
      return { targets: {}, target_items: [] };
    }

    return {
      targets: result.data?.targets || {},
      target_items: result.data?.target_items || [],
    };
  }, []);

  // 認証データ取得
  const getAuthData = useCallback(() => {
    const token = Cookies.get('auth_token');
    const userInfoStr = Cookies.get('user_info');
    
    if (!token || !userInfoStr) {
      return null;
    }
    
    try {
      const userInfo = JSON.parse(userInfoStr);
      return { token, user: userInfo };
    } catch (error) {
      console.error('Cookie parse error:', error);
      return null;
    }
  }, []);

  // ユーザー一覧取得
  useEffect(() => {
    const fetchUsersList = async () => {
      try {
        setUsersLoading(true);
        const result = await fetchUsers({
          limit: 100,
          sort_by: 'name',
          sort_order: 'asc'
        });
        
        if (result.status === 'success' && result.data.users) {
          const filteredUsers = result.data.users.filter(
            (user) => !EXCLUDED_ADMIN_ROLES.includes(user.role)
          );
          setUsers(filteredUsers);
        } else {
          setError('ユーザー一覧の取得に失敗しました');
        }
      } catch (err) {
        console.error('Failed to fetch users:', err);
        setError('ユーザー一覧の取得に失敗しました');
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsersList();
  }, []);

  // データ取得
  const fetchAnalyticsData = useCallback(async () => {
    const auth = getAuthData();
    
    if (!auth) {
      setError('ログインが必要です');
      setLoading(false);
      return;
    }

    if (!selectedUserId) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const periodInfo = getPeriodRange(period, customFrom, customTo);

      if (!periodInfo) {
        setError('開始日と終了日を指定してください');
        setLoading(false);
        return;
      }

      const pageLimit = 100;
      let offset = 0;
      let total = 0;
      const reports = [];

      while (true) {
        const params = new URLSearchParams({
          start_date: periodInfo.from,
          end_date: periodInfo.to,
          limit: String(pageLimit),
          offset: String(offset),
          user_id: String(selectedUserId),
        });

        const response = await fetch(`${DAILY_REPORT_API}?${params.toString()}`, {
          headers: {
            'Authorization': `Bearer ${auth.token}`,
          },
        });

        const result = await response.json();
        if (result.status !== 'success') {
          setError(result.message || 'データ取得に失敗しました');
          return;
        }

        const batch = result.data?.reports || [];
        total = Number(result.data?.total || 0);
        reports.push(...batch);

        if (batch.length === 0 || reports.length >= total) {
          break;
        }

        offset += pageLimit;
      }

      if (reports.length >= 0) {
        const selectedUser = users.find(user => String(user.id) === String(selectedUserId));
        const analytics = buildAnalyticsFromReports(reports);
        const targets = await fetchTargetsForPeriod(auth.token, selectedUserId, periodInfo);
        if (!analytics) {
          setData(null);
          setError('分析データが見つかりませんでした');
          return;
        }

        setData({
          user: selectedUser ? { name: selectedUser.name } : undefined,
          period: periodInfo,
          metrics: analytics.metrics || [],
          summary: analytics.summary || {},
          daily_data: analytics.daily_data || [],
          targets: targets.targets || {},
          target_items: targets.target_items || [],
        });
        const metrics = analytics.metrics || [];
        setSelectedMetric(metrics[0] || '');
        setSelectedXMetric(metrics[0] || '');
        setSelectedYMetric(metrics[1] || metrics[0] || '');
        setError('');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [period, customFrom, customTo, getAuthData, selectedUserId, users, fetchTargetsForPeriod]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedUserId, period, customFrom, customTo, fetchAnalyticsData]);

  const resolveComparisonBaseDate = useCallback(() => {
    const periodInfo = getPeriodRange(period, customFrom, customTo);
    if (!periodInfo) {
      return null;
    }

    if (period === 'last_month') {
      return periodInfo.from;
    }

    if (period === 'custom') {
      return periodInfo.to || periodInfo.from;
    }

    return moment().format('YYYY-MM-DD');
  }, [period, customFrom, customTo]);

  // 複数月データ取得
  const fetchMultiMonthDataForUser = useCallback(async () => {
    const auth = getAuthData();
    const baseDate = resolveComparisonBaseDate();
    
    if (!auth || !selectedUserId || !baseDate) {
      setMultiMonthData(null);
      return;
    }

    try {
      const result = await fetchMultiMonthAnalytics(auth.token, DAILY_REPORT_API, selectedUserId, baseDate);
      setMultiMonthData(result);
    } catch (err) {
      console.error('Failed to fetch multi-month data:', err);
      setMultiMonthData(null);
    }
  }, [selectedUserId, getAuthData, resolveComparisonBaseDate]);

  useEffect(() => {
    if (selectedUserId) {
      fetchMultiMonthDataForUser();
    }
  }, [selectedUserId, fetchMultiMonthDataForUser]);

  // 日別推移グラフデータ生成
  const generateChartData = useCallback(() => {
    if (!data?.period?.from || !data?.period?.to || !selectedMetric || data.metrics.length === 0) return null;

    const metricLabels = data.metrics.reduce((acc, key) => {
      acc[key] = key;
      return acc;
    }, {});

    // 期間の開始日から終了日までの全日付を生成
    const startDate = moment(data.period.from);
    const endDate = moment(data.period.to);
    const allDates = [];
    const labels = [];
    
    let currentDate = startDate.clone();
    while (currentDate.isSameOrBefore(endDate, 'day')) {
      allDates.push(currentDate.format('YYYY-MM-DD'));
      labels.push(currentDate.format('D'));
      currentDate.add(1, 'day');
    }

    // 日別データをマップに変換
    const dailyDataMap = {};
    if (data.daily_data) {
      data.daily_data.forEach(d => {
        dailyDataMap[d.date] = d;
      });
    }

    // 全日付に対してデータを生成（データがない日はnull）
    const dataValues = allDates.map(date => {
      const dayData = dailyDataMap[date];
      if (!dayData) return null;
      
      return dayData[selectedMetric] || null;
    });

    const validValues = dataValues.filter((value) => Number.isFinite(value));
    const averageValue = validValues.length > 0
      ? validValues.reduce((sum, value) => sum + value, 0) / validValues.length
      : null;

    const averageLineValues = allDates.map(() => averageValue);

    return {
      labels,
      datasets: [
        {
          label: metricLabels[selectedMetric] || selectedMetric,
          data: dataValues,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: 'rgb(59, 130, 246)',
        },
        {
          label: '平均値',
          data: averageLineValues,
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 2,
        },
      ],
      metricOptions: data.metrics.map(metric => ({
        value: metric,
        label: metric
      }))
    };
  }, [data, selectedMetric]);
  const chartData = generateChartData();

  const generateRegressionAnalysis = useCallback(() => {
    if (!data?.daily_data || !selectedXMetric || !selectedYMetric || selectedXMetric === selectedYMetric) {
      return null;
    }

    const isTargetDay = (dateStr) => {
      if (regressionDayType === 'all') return true;
      const date = moment(dateStr, 'YYYY-MM-DD', true);
      if (!date.isValid()) return false;
      const day = date.day();
      const isWeekend = day === 0 || day === 6;
      if (regressionDayType === 'weekdays') return !isWeekend;
      if (regressionDayType === 'weekends') return isWeekend;
      return true;
    };

    const points = data.daily_data.reduce((acc, dayData) => {
      if (!isTargetDay(dayData?.date)) {
        return acc;
      }

      const x = Number(dayData[selectedXMetric]);
      const y = Number(dayData[selectedYMetric]);

      if (Number.isFinite(x) && Number.isFinite(y) && x > 0) {
        acc.push({ x, y });
      }

      return acc;
    }, []);

    if (points.length < 2) {
      return {
        chartData: null,
        slope: null,
        intercept: null,
        r2: null,
        sampleSize: points.length,
      };
    }

    const n = points.length;
    const sumX = points.reduce((sum, point) => sum + point.x, 0);
    const sumY = points.reduce((sum, point) => sum + point.y, 0);
    const sumXY = points.reduce((sum, point) => sum + (point.x * point.y), 0);
    const sumXX = points.reduce((sum, point) => sum + (point.x * point.x), 0);
    const denominator = (n * sumXX) - (sumX * sumX);

    if (denominator === 0) {
      return {
        chartData: null,
        slope: null,
        intercept: null,
        r2: null,
        sampleSize: points.length,
      };
    }

    const slope = ((n * sumXY) - (sumX * sumY)) / denominator;
    const intercept = (sumY - (slope * sumX)) / n;

    const meanY = sumY / n;
    const ssTotal = points.reduce((sum, point) => sum + ((point.y - meanY) ** 2), 0);
    const ssResidual = points.reduce((sum, point) => {
      const predictedY = (slope * point.x) + intercept;
      return sum + ((point.y - predictedY) ** 2);
    }, 0);
    const r2 = ssTotal === 0 ? 1 : 1 - (ssResidual / ssTotal);

    const xValues = points.map(point => point.x);
    const maxX = Math.max(...xValues);

    const lineStartX = 0;
    const lineEndX = maxX;

    const regressionLine = [
      { x: lineStartX, y: intercept },
      { x: lineEndX, y: (slope * lineEndX) + intercept },
    ];

    const sortedNumbers = (values) => [...values].sort((a, b) => a - b);
    const median = (values) => {
      if (!values.length) return null;
      const sorted = sortedNumbers(values);
      const mid = Math.floor(sorted.length / 2);
      if (sorted.length % 2 === 0) {
        return (sorted[mid - 1] + sorted[mid]) / 2;
      }
      return sorted[mid];
    };

    const slopeCandidates = [];
    for (let i = 0; i < points.length - 1; i += 1) {
      for (let j = i + 1; j < points.length; j += 1) {
        const dx = points[j].x - points[i].x;
        if (dx !== 0) {
          slopeCandidates.push((points[j].y - points[i].y) / dx);
        }
      }
    }

    const robustSlope = median(slopeCandidates);
    const robustIntercept = Number.isFinite(robustSlope)
      ? median(points.map(point => point.y - (robustSlope * point.x)))
      : null;

    let robustLine = null;
    let robustR2 = null;

    if (Number.isFinite(robustSlope) && Number.isFinite(robustIntercept)) {
      const robustLineStartX = 0;
      const robustLineEndX = maxX;

      robustLine = [
        { x: robustLineStartX, y: robustIntercept },
        { x: robustLineEndX, y: (robustSlope * robustLineEndX) + robustIntercept },
      ];

      const robustSsResidual = points.reduce((sum, point) => {
        const predictedY = (robustSlope * point.x) + robustIntercept;
        return sum + ((point.y - predictedY) ** 2);
      }, 0);
      robustR2 = ssTotal === 0 ? 1 : 1 - (robustSsResidual / ssTotal);
    }

    const datasets = [
      {
        label: '実測値',
        data: points,
        backgroundColor: 'rgba(59, 130, 246, 0.7)',
        pointRadius: 4,
      },
      {
        label: '最小二乗法の回帰直線',
        data: regressionLine,
        type: 'line',
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        pointRadius: 0,
        borderWidth: 2,
        tension: 0,
      },
    ];

    if (robustLine) {
      datasets.push({
        label: 'ロバスト回帰直線（Theil-Sen）',
        data: robustLine,
        type: 'line',
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        pointRadius: 0,
        borderWidth: 2,
        tension: 0,
        borderDash: [6, 6],
      });

    }

    return {
      chartData: {
        datasets,
      },
      slope,
      intercept,
      r2,
      robustSlope,
      robustIntercept,
      robustR2,
      sampleSize: points.length,
    };
  }, [data, selectedXMetric, selectedYMetric, regressionDayType]);

  const regressionAnalysis = generateRegressionAnalysis();
  const formatRegressionValue = (value) => (Number.isFinite(value) ? value.toFixed(3) : '-');

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* ヘッダー */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">ユーザー分析</h1>
          <p className="text-gray-600 mt-1">個別ユーザーの分析データを表示します</p>
        </div>

        {/* ユーザー選択セクション */}
        <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* ユーザー選択 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ユーザー選択
              </label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                disabled={usersLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-100"
              >
                <option value="">ユーザーを選択してください</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
              {usersLoading && <p className="text-xs text-gray-500 mt-1">ユーザー一覧を読み込み中...</p>}
            </div>

            {/* 期間選択 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                期間
              </label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="today">今日</option>
                <option value="this_week">今週</option>
                <option value="this_month">今月</option>
                <option value="last_month">先月</option>
                <option value="this_year">今年</option>
                <option value="custom">カスタム</option>
              </select>
            </div>

            {/* カスタム期間 */}
            {period === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    開始日
                  </label>
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    終了日
                  </label>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* エラー表示 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* データがない場合のメッセージ */}
        {!selectedUserId && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
            <p className="text-blue-700">ユーザーを選択してください</p>
          </div>
        )}

        {/* 分析データ表示 */}
        {selectedUserId && data && !loading && (
          <>
            <SummaryCards data={data} />

            {/* 月別比較 */}
            {multiMonthData && (
              <MultiMonthComparison multiMonthData={multiMonthData} mode="attendance-per-day" />
            )}

            {/* 日別推移グラフ */}
            {chartData && (
              <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">日別推移</h3>
                  <select
                    value={selectedMetric}
                    onChange={(e) => setSelectedMetric(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-xs md:text-sm"
                  >
                    {chartData.metricOptions?.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="h-64 md:h-80">
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: true, position: 'top' },
                      },
                      scales: {
                        y: { beginAtZero: true },
                      },
                    }}
                  />
                </div>
              </div>
            )}

            {/* 相関分析（最小二乗法） */}
            {data.metrics?.length >= 2 && (
              <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">相関分析（最小二乗法 / ロバスト回帰）</h3>
                  <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                    <select
                      value={regressionDayType}
                      onChange={(e) => setRegressionDayType(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-xs md:text-sm"
                    >
                      <option value="all">全日</option>
                      <option value="weekdays">平日</option>
                      <option value="weekends">土日</option>
                    </select>
                    <select
                      value={selectedXMetric}
                      onChange={(e) => {
                        const nextXMetric = e.target.value;
                        setSelectedXMetric(nextXMetric);
                        if (nextXMetric === selectedYMetric) {
                          const fallbackY = data.metrics.find(metric => metric !== nextXMetric) || nextXMetric;
                          setSelectedYMetric(fallbackY);
                        }
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-xs md:text-sm"
                    >
                      {data.metrics.map(metric => (
                        <option key={`x-${metric}`} value={metric}>{`横軸: ${metric}`}</option>
                      ))}
                    </select>
                    <select
                      value={selectedYMetric}
                      onChange={(e) => {
                        const nextYMetric = e.target.value;
                        setSelectedYMetric(nextYMetric);
                        if (nextYMetric === selectedXMetric) {
                          const fallbackX = data.metrics.find(metric => metric !== nextYMetric) || nextYMetric;
                          setSelectedXMetric(fallbackX);
                        }
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-xs md:text-sm"
                    >
                      {data.metrics.map(metric => (
                        <option key={`y-${metric}`} value={metric}>{`縦軸: ${metric}`}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {regressionAnalysis?.chartData ? (
                  <>
                    <div className="mb-3 text-xs md:text-sm text-gray-700">
                      <span className="mr-4">回帰式: y = {formatRegressionValue(regressionAnalysis.slope)}x + {formatRegressionValue(regressionAnalysis.intercept)}</span>
                      <span className="mr-4">決定係数 (R²): {formatRegressionValue(regressionAnalysis.r2)}</span>
                      <span>サンプル数: {regressionAnalysis.sampleSize}</span>
                    </div>
                    <div className="mb-3 text-xs md:text-sm text-amber-700">
                      <span className="mr-4">ロバスト回帰式: y = {formatRegressionValue(regressionAnalysis.robustSlope)}x + {formatRegressionValue(regressionAnalysis.robustIntercept)}</span>
                      <span>ロバスト R²: {formatRegressionValue(regressionAnalysis.robustR2)}</span>
                    </div>
                    <div className="h-64 md:h-80">
                      <Scatter
                        data={regressionAnalysis.chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: true, position: 'top' },
                          },
                          scales: {
                            x: {
                              type: 'linear',
                              min: 0,
                              title: {
                                display: true,
                                text: selectedXMetric,
                              },
                            },
                            y: {
                              grid: {
                                color: (context) => (context.tick.value === 0 ? 'rgba(0, 0, 0, 0.55)' : 'rgba(0, 0, 0, 0.12)'),
                              },
                              title: {
                                display: true,
                                text: selectedYMetric,
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-sm text-gray-600">
                    回帰直線を算出できるデータが不足しています（同一値のみ、または有効なデータ点が2件未満）。
                  </div>
                )}
              </div>
            )}

            {/* 比率計算 */}
            <RatioCalculator
              metrics={data.metrics}
              summary={data.summary}
              daily_data={data.daily_data}
              targets={data.targets}
              target_items={data.target_items}
            />

          </>
        )}

        {/* ローディング状態 */}
        {selectedUserId && loading && (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <p className="text-gray-600">データを読み込み中...</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminAnalytics;

const SummaryCards = ({ data }) => {
  const resolveTargetValue = (metric) => {
    const targetsMap = data.targets || {};
    const targetItems = data.target_items || [];

    if (targetsMap[metric] != null) {
      return Number(targetsMap[metric] || 0);
    }

    const prefixed = `target_${metric}`;
    if (targetsMap[prefixed] != null) {
      return Number(targetsMap[prefixed] || 0);
    }

    const normalizedMetric = String(metric).toLowerCase();
    const item = targetItems.find((row) => {
      const key = String(row.target_key || '').toLowerCase();
      const label = String(row.target_label || '').toLowerCase();
      return key === prefixed || key === normalizedMetric || label === normalizedMetric || label.includes(normalizedMetric);
    });

    return Number(item?.target_value || 0);
  };

  const sortedMetrics = [...(data.metrics || [])].filter((metric) => metric !== 'working_days').sort((a, b) => {
    const valueA = Number(data.summary?.[a] || 0);
    const valueB = Number(data.summary?.[b] || 0);

    if (valueB !== valueA) {
      return valueB - valueA;
    }

    return String(a).localeCompare(String(b), 'ja');
  });

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
      <SummaryCard
        key="working_days"
        title="出勤日数"
        value={data.summary?.working_days || 0}
        target={null}
        color="blue"
      />
      {sortedMetrics.map((metric, index) => (
        <SummaryCard
          key={metric}
          title={metric}
          value={data.summary?.[metric] || 0}
          target={resolveTargetValue(metric)}
          color={pickSummaryColor(index + 1)}
        />
      ))}
    </div>
  );
};

const SummaryCard = ({ title, value, target, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    green: 'bg-green-50 border-green-200 text-green-600',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-600',
    red: 'bg-red-50 border-red-200 text-red-600',
  };

  return (
    <div className={`rounded-lg border-2 p-3 md:p-4 ${colorClasses[color]}`}>
      <h3 className="text-xs md:text-sm font-medium mb-1 md:mb-2">{title}</h3>
      <p className="text-xl md:text-3xl font-bold mb-1">{Number(value || 0).toLocaleString()}</p>
      <p className="text-[10px] md:text-xs opacity-75">目標: {target == null ? '-' : Number(target || 0).toLocaleString()}</p>
    </div>
  );
};

const pickSummaryColor = (index) => {
  const colors = ['blue', 'green', 'yellow', 'red'];
  return colors[index % colors.length];
};

const getPeriodRange = (period, customFrom, customTo) => {
  const today = moment();

  if (period === 'custom') {
    if (!customFrom || !customTo) return null;
    return { from: customFrom, to: customTo, label: 'カスタム' };
  }

  if (period === 'today') {
    return {
      from: today.format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD'),
      label: '今日'
    };
  }

  if (period === 'this_week') {
    return {
      from: today.clone().startOf('week').format('YYYY-MM-DD'),
      to: today.clone().endOf('week').format('YYYY-MM-DD'),
      label: '今週'
    };
  }

  if (period === 'this_month') {
    return {
      from: today.clone().startOf('month').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD'),
      label: '今月'
    };
  }

  if (period === 'last_month') {
    const lastMonth = today.clone().subtract(1, 'month');
    return {
      from: lastMonth.startOf('month').format('YYYY-MM-DD'),
      to: lastMonth.endOf('month').format('YYYY-MM-DD'),
      label: '先月'
    };
  }

  if (period === 'this_year') {
    return {
      from: today.clone().startOf('year').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD'),
      label: '今年'
    };
  }

  return null;
};
